import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';

import { 
  Role, 
  RoleSchema, 
  CreateRoleRequest, 
  UpdateRoleRequest
} from '@/models/role';

/**
 * Repository pour gérer les rôles dans Firestore
 */
export class RoleRepository {
  private static readonly COLLECTION_NAME = 'roles';

  /**
   * Récupère tous les rôles
   */
  static async getAll(): Promise<Role[]> {
    try {
      console.log(`🔍 Récupération de tous les rôles`);
      
      const querySnapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      const roles: Role[] = [];

      querySnapshot.forEach((doc) => {
        const data = {
          id: doc.id,
          ...doc.data(),
        };
        try {
          roles.push(RoleSchema.parse(data));
        } catch (error) {
          console.warn(`⚠️ Rôle ${doc.id} invalide, ignoré:`, error);
        }
      });

      // Trier par nom
      roles.sort((a, b) => a.name.localeCompare(b.name));

      console.log(`✅ ${roles.length} rôles trouvés`);
      return roles;

    } catch (error) {
      console.error(`❌ Erreur lors de la récupération des rôles:`, error);
      throw error;
    }
  }

  /**
   * Récupère un rôle par son nom
   */
  static async getByName(name: string): Promise<Role | null> {
    try {
      console.log(`🔍 Récupération du rôle: ${name}`);
      
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('name', '==', name)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log(`❌ Aucun rôle trouvé avec le nom: ${name}`);
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = {
        id: doc.id,
        ...doc.data(),
      };

      const role = RoleSchema.parse(data);
      console.log(`✅ Rôle trouvé: ${name}`);
      return role;

    } catch (error) {
      console.error(`❌ Erreur lors de la récupération du rôle:`, error);
      throw error;
    }
  }

  /**
   * Récupère un rôle par son ID
   */
  static async getById(roleId: string): Promise<Role | null> {
    try {
      console.log(`🔍 Récupération du rôle par ID: ${roleId}`);
      
      const docRef = doc(db, this.COLLECTION_NAME, roleId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.log(`❌ Aucun rôle trouvé avec l'ID: ${roleId}`);
        return null;
      }

      const data = {
        id: docSnap.id,
        ...docSnap.data(),
      };

      const role = RoleSchema.parse(data);
      console.log(`✅ Rôle trouvé: ${roleId}`);
      return role;

    } catch (error) {
      console.error(`❌ Erreur lors de la récupération du rôle:`, error);
      throw error;
    }
  }

  /**
   * Crée un nouveau rôle
   */
  static async create(request: CreateRoleRequest): Promise<Role> {
    try {
      console.log(`🔍 Création d'un nouveau rôle: ${request.name}`);
      
      // Vérifier si le rôle existe déjà
      const existingRole = await this.getByName(request.name);
      if (existingRole) {
        throw new Error(`Un rôle avec le nom "${request.name}" existe déjà`);
      }

      const roleData = {
        name: request.name,
        displayName: request.displayName,
        description: request.description || undefined,
        permissions: request.permissions,
        isSystem: request.isSystem || false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), roleData);
      
      // Récupérer le rôle créé avec les timestamps résolus
      const createdDoc = await getDoc(docRef);
      const data = {
        id: docRef.id,
        ...createdDoc.data(),
      };

      const role = RoleSchema.parse(data);
      console.log(`✅ Rôle créé avec succès: ${role.id}`);
      return role;

    } catch (error) {
      console.error(`❌ Erreur lors de la création du rôle:`, error);
      throw error;
    }
  }

  /**
   * Met à jour un rôle existant
   */
  static async update(roleId: string, request: UpdateRoleRequest): Promise<Role> {
    try {
      console.log(`🔍 Mise à jour du rôle: ${roleId}`);
      
      const role = await this.getById(roleId);
      if (!role) {
        throw new Error('Rôle non trouvé');
      }

      // Vérifier si c'est un rôle système (non modifiable)
      if (role.isSystem) {
        throw new Error('Les rôles système ne peuvent pas être modifiés');
      }

      const updateData: any = {
        updatedAt: serverTimestamp(),
      };

      if (request.displayName !== undefined) {
        updateData.displayName = request.displayName;
      }
      if (request.description !== undefined) {
        updateData.description = request.description;
      }
      if (request.permissions !== undefined) {
        // Fusionner les permissions existantes avec les nouvelles
        updateData.permissions = {
          ...role.permissions,
          ...request.permissions,
        };
      }

      const docRef = doc(db, this.COLLECTION_NAME, roleId);
      await updateDoc(docRef, updateData);
      
      // Récupérer le rôle mis à jour
      const updatedDoc = await getDoc(docRef);
      if (!updatedDoc.exists()) {
        throw new Error('Rôle non trouvé après mise à jour');
      }

      const data = {
        id: roleId,
        ...updatedDoc.data(),
      };

      const updatedRole = RoleSchema.parse(data);
      console.log(`✅ Rôle mis à jour avec succès: ${roleId}`);
      return updatedRole;

    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour du rôle:`, error);
      throw error;
    }
  }

  /**
   * Supprime un rôle
   */
  static async delete(roleId: string): Promise<void> {
    try {
      console.log(`🔍 Suppression du rôle: ${roleId}`);
      
      const role = await this.getById(roleId);
      if (!role) {
        throw new Error('Rôle non trouvé');
      }

      // Vérifier si c'est un rôle système (non supprimable)
      if (role.isSystem) {
        throw new Error('Les rôles système ne peuvent pas être supprimés');
      }

      const docRef = doc(db, this.COLLECTION_NAME, roleId);
      await deleteDoc(docRef);
      
      console.log(`✅ Rôle supprimé avec succès: ${roleId}`);

    } catch (error) {
      console.error(`❌ Erreur lors de la suppression du rôle:`, error);
      throw error;
    }
  }

  /**
   * Initialise les rôles système par défaut
   * Cette méthode doit être appelée une fois lors de la migration
   * Note: Les rôles doivent être créés manuellement dans Firestore ou via le script de migration
   */
  static async initializeSystemRoles(): Promise<Role[]> {
    try {
      console.log(`🔍 Initialisation des rôles système`);
      console.warn(`⚠️ Cette méthode nécessite que les rôles soient déjà créés dans Firestore`);
      console.warn(`⚠️ Utilisez le script de migration pour créer les rôles système`);
      
      // Récupérer tous les rôles existants
      const allRoles = await this.getAll();
      const systemRoles = allRoles.filter(role => role.isSystem);
      
      console.log(`✅ ${systemRoles.length} rôles système trouvés`);
      return systemRoles;

    } catch (error) {
      console.error(`❌ Erreur lors de l'initialisation des rôles système:`, error);
      throw error;
    }
  }
}

